import { Box, Button, TextField } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { Music as MusicIcon } from "lucide-react";
import React, { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  ExperienceErrorText,
  ExperienceFieldGuide,
  ExperienceFieldLabel,
  ExperienceHeader,
  ExperienceMediaCard,
  ExperienceOptionalLabel,
  ExperienceShell,
  ExperienceUploadZone,
  type ExperienceMomentVariant,
  fieldHelperText,
  getExperienceFieldSx,
  getExperienceTheme,
  getFloatingActionSx,
} from "./ExperienceUI";
import ImageUpload from "@/shared/components/file-upload/ImageUpload";

interface AudioData {
  audioUrl?: string;
  description?: string;
  songTitle?: string;
  artistName?: string;

  /**
   * Lyrics are now entered directly as text.
   */
  lyrics_text?: string;

  /*
   * lyricsUrl?: string | File;
   */
  image_url?: string;
}

interface AudioExperienceProps {
  template?: string;
  darkMode: boolean;
  data?: AudioData;
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: AudioData) => void;
}

const getValidationSchema = (t: any) =>
  Yup.object({
    audioUrl: Yup.string().required(t("audio_is_required")),
    description: Yup.string().nullable(),
  });

const AudioExperience: React.FC<AudioExperienceProps> = ({
  template,
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");

  const theme = getExperienceTheme(darkMode);

  const validationSchema = getValidationSchema(t);

  const isInternalUpdateRef = useRef(false);

  const instanceId = useId().replace(/:/g, "");

  const audioUploadId = `audio-upload-${instanceId}`;
  const imageUploadId = `image-upload-${instanceId}`;

  const fieldSx = getExperienceFieldSx(darkMode);

  const singleCardTemplate = t("single_card_template");

  const kIndieCardTemplate = t("k_indie_card_template");

  const listeningTogetherTemplate = t("listening_together_card_template");

  const isSingleCardTemplate =
    template === singleCardTemplate || template === "Single Card Template";

  const isKIndieCardTemplate =
    template === kIndieCardTemplate || template === "K-Indie Card Template";

  const isListeningTogetherTemplate =
    template === listeningTogetherTemplate || template === "Listening Together Card Template";

  /*
   * Lyrics are available for these templates.
   */
  // const showLyricsUpload = isSingleCardTemplate || isKIndieCardTemplate;

  const formik = useFormik<AudioData>({
    initialValues: {
      audioUrl: data?.audioUrl ?? "",

      description: data?.description ?? "",

      songTitle: data?.songTitle ?? "",

      artistName: data?.artistName ?? "",

      /*
       * Lyrics are now normal text.
       */
      lyrics_text: data?.lyrics_text ?? "",
      image_url: data?.image_url ?? "",
    },

    enableReinitialize: true,

    validationSchema,

    validateOnChange: false,

    validateOnBlur: true,

    onSubmit: () => {},
  });

  /*
   * ---------------------------------------------------------
   * SYNC DATA FROM PARENT
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const newValues: AudioData = {
        audioUrl: data.audioUrl ?? "",

        description: data.description ?? "",

        songTitle: data.songTitle ?? "",

        artistName: data.artistName ?? "",

        lyrics_text: data.lyrics_text ?? "",

        image_url: data.image_url ?? "",
      };

      const hasChanged = Object.keys(newValues).some(
        (key) => formik.values[key as keyof AudioData] !== newValues[key as keyof AudioData]
      );

      if (hasChanged) {
        formik.setValues(newValues);
      }
    }

    isInternalUpdateRef.current = false;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /*
   * ---------------------------------------------------------
   * SEND DATA TO PARENT
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (onDataChange) {
      const timeoutId = setTimeout(() => {
        isInternalUpdateRef.current = true;

        onDataChange({
          audioUrl: formik.values.audioUrl,

          description: formik.values.description,

          songTitle: formik.values.songTitle,

          artistName: formik.values.artistName,

          /*
           * Send lyrics directly as text.
           */
          lyrics_text: formik.values.lyrics_text,

          image_url: formik.values.image_url,
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  /*
   * ---------------------------------------------------------
   * AUDIO FILE SELECT
   * ---------------------------------------------------------
   */

  const handleFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];

      const reader = new FileReader();

      reader.onloadend = () => {
        const result = reader.result as string;

        formik.setFieldValue("audioUrl", result);

        formik.setFieldError("audioUrl", undefined);
      };

      reader.readAsDataURL(file);
    }
  };

  /*
   * ---------------------------------------------------------
   * REMOVE AUDIO
   * ---------------------------------------------------------
   */

  const handleRemoveAudio = () => {
    formik.setFieldValue("audioUrl", "");

    formik.setFieldTouched("audioUrl", true);

    formik.setFieldError("audioUrl", t("audio_is_required"));
  };

  const handleRemoveImage = () => {
    formik.setFieldValue("image_url", "");
    formik.setFieldTouched("image_url", true);
  };

  /*
   * ---------------------------------------------------------
   * IMAGE FILE SELECT
   * ---------------------------------------------------------
   */

  const handleImageSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];

      const reader = new FileReader();

      reader.onloadend = () => {
        const result = reader.result as string;

        formik.setFieldValue("image_url", result);

        formik.setFieldError("image_url", undefined);
      };

      reader.readAsDataURL(file);
    }
  };

  /*
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    <FormikProvider value={formik}>
      <ExperienceShell>
        <ExperienceHeader
          darkMode={darkMode}
          icon={MusicIcon}
          title={t("audio")}
          subtitle={
            isListeningTogetherTemplate
              ? t("audio_header_subtitle_listening")
              : t("audio_header_subtitle")
          }
        />

        <ExperienceFieldGuide darkMode={darkMode}>
          {isListeningTogetherTemplate ? t("audio_field_guide_listening") : t("audio_field_guide")}
        </ExperienceFieldGuide>

        {/* -------------------------------------------------- */}
        {/* AUDIO */}
        {/* -------------------------------------------------- */}

        {formik.values.audioUrl ? (
          <ExperienceMediaCard
            darkMode={darkMode}
            actions={
              <Button
                variant="contained"
                onClick={handleRemoveAudio}
                size="small"
                sx={getFloatingActionSx(darkMode)}
              >
                {t("remove_audio")}
              </Button>
            }
          >
            <Box sx={{ p: 2 }}>
              <audio
                controls
                style={{
                  width: "100%",
                }}
              >
                <source src={formik.values.audioUrl} />

                {t("audio_browser_not_supported")}
              </audio>
            </Box>
          </ExperienceMediaCard>
        ) : (
          <ExperienceUploadZone
            darkMode={darkMode}
            hasError={Boolean(formik.touched.audioUrl && formik.errors.audioUrl)}
            icon={MusicIcon}
            title={
              isListeningTogetherTemplate
                ? t("audio_upload_title_listening")
                : t("audio_upload_title")
            }
            subtitle={
              isListeningTogetherTemplate
                ? t("audio_upload_subtitle_listening")
                : t("drag_and_drop_audio_to_upload")
            }
            onFileSelect={handleFileSelect}
            accept="audio/*"
            inputId={audioUploadId}
            buttonLabel={t("choose_file")}
          />
        )}

        {formik.touched.audioUrl && formik.errors.audioUrl && (
          <ExperienceErrorText darkMode={darkMode} sx={{ mt: -1.5 }}>
            {formik.errors.audioUrl}
          </ExperienceErrorText>
        )}

        {/* -------------------------------------------------- */}
        {/* SONG TITLE / ARTIST / LYRICS */}
        {/* -------------------------------------------------- */}

        <>
          <TextField
            label={t("audio_song_title")}
            name="songTitle"
            value={formik.values.songTitle || ""}
            onChange={formik.handleChange}
            fullWidth
            placeholder={t("audio_song_title_placeholder")}
            helperText={t("audio_song_title_helper")}
            sx={fieldSx}
          />

          <TextField
            label={t("audio_artist_name")}
            name="artistName"
            value={formik.values.artistName || ""}
            onChange={formik.handleChange}
            fullWidth
            placeholder={t("audio_artist_name_placeholder")}
            sx={fieldSx}
          />

          {/* ------------------------------------------------ */}
          {/* LYRICS TEXT */}
          {/* ------------------------------------------------ */}

          <TextField
            label="Lyrics"
            name="lyrics_text"
            value={formik.values.lyrics_text || ""}
            onChange={formik.handleChange}
            fullWidth
            multiline
            rows={5}
            placeholder="Enter lyrics..."
            helperText="Enter the lyrics as normal text."
            sx={fieldSx}
          />
        </>

        {/* -------------------------------------------------- */}
        {/* DESCRIPTION */}
        {/* -------------------------------------------------- */}

        <TextField
          label={t("audio_caption_label")}
          name="description"
          value={formik.values.description}
          onChange={(e) => {
            formik.handleChange(e);

            formik.setFieldError("description", undefined);
          }}
          onBlur={formik.handleBlur}
          fullWidth
          multiline
          minRows={4}
          placeholder={
            isListeningTogetherTemplate
              ? t("audio_description_placeholder_listening")
              : t("audio_description_placeholder")
          }
          error={Boolean(formik.touched.description && formik.errors.description)}
          helperText={fieldHelperText(
            formik.touched.description && formik.errors.description
              ? String(formik.errors.description)
              : undefined,
            t("audio_description_helper")
          )}
          sx={fieldSx}
        />

        {/* -------------------------------------------------- */}
        {/* IMAGE (optional — uploaded on form submit) */}
        {/* -------------------------------------------------- */}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <ExperienceFieldLabel darkMode={darkMode}>
            {t("image_upload_title")}
            <ExperienceOptionalLabel darkMode={darkMode} />
          </ExperienceFieldLabel>

          {formik.values.image_url ? (
            <ExperienceMediaCard
              darkMode={darkMode}
              actions={
                <Button
                  variant="contained"
                  onClick={handleRemoveImage}
                  size="small"
                  sx={getFloatingActionSx(darkMode)}
                >
                  {t("remove_image", { ns: "talent", defaultValue: "Remove image" })}
                </Button>
              }
            >
              <Box sx={{ p: 2 }}>
                <img
                  src={formik.values.image_url}
                  alt={t("image_upload_title")}
                  style={{
                    width: "100%",
                  }}
                />
              </Box>
            </ExperienceMediaCard>
          ) : (
            <ImageUpload
              onFileSelect={handleImageSelect}
              name={imageUploadId}
              accept="image/*"
              multiple={false}
              label={t("image_upload_title")}
              subLabel={t("image_upload_subtitle")}
              browseButtonLabel={t("browse_image")}
              layout="column"
            />
          )}
        </Box>
      </ExperienceShell>
    </FormikProvider>
  );
};

export default AudioExperience;
